import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useAuth } from "./AuthContext";
import { useTenantContext } from "./TenantContext";
import { subscriptionApi } from "../services/api";

const SubscriptionContext = createContext(null);

/**
 * Extract data from API response (handles both casing conventions)
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  return { success, data };
};

/**
 * Provides subscription state, feature flags, and pricing info
 * for the current tenant.
 */
export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { currentTenantId, selectedTenant } = useTenantContext();

  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load subscription when tenant changes
  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated && currentTenantId) {
      loadSubscription();
    } else {
      setSubscription(null);
      setFeatures({});
      setLoading(false);
    }
  }, [isAuthenticated, currentTenantId, authLoading]);

  const loadSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await subscriptionApi.getCurrent();
      const { success, data } = extractData(response);

      if (success && data) {
        setSubscription(data);
        setFeatures(data.features || {});
      }
    } catch (err) {
      console.error("[SubscriptionContext] Failed to load subscription:", err);
      if (err.status !== 401) {
        setError(err.message || "Failed to load subscription");
      }
    } finally {
      setLoading(false);
    }
  }, [currentTenantId]);

  const refreshSubscription = useCallback(async () => {
    await loadSubscription();
  }, [loadSubscription]);

  /**
   * Check if a specific feature is available
   */
  const canAccess = useCallback(
    (featureName) => {
      return features[featureName] === true;
    },
    [features],
  );

  /**
   * Check if the current tier is at least the given tier
   */
  const isAtLeastTier = useCallback(
    (tier) => {
      const tierOrder = { Free: 0, Pro: 1, Enterprise: 2 };
      const currentTierLevel =
        tierOrder[subscription?.effectiveTier] ?? tierOrder.Free;
      const requiredTierLevel = tierOrder[tier] ?? 0;
      return currentTierLevel >= requiredTierLevel;
    },
    [subscription?.effectiveTier],
  );

  /**
   * Convenience flags
   */
  const isFreeTier = subscription?.effectiveTier === "Free";
  const isProTier = subscription?.effectiveTier === "Pro";
  const isEnterpriseTier = subscription?.effectiveTier === "Enterprise";
  const isTrial = subscription?.status === "Trial";
  const isTrialExpired =
    isTrial && subscription?.trialEndsOn
      ? new Date(subscription.trialEndsOn) < new Date()
      : false;
  const trialDaysRemaining = useMemo(() => {
    if (!isTrial || !subscription?.trialEndsOn) return null;
    const end = new Date(subscription.trialEndsOn);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  }, [isTrial, subscription?.trialEndsOn]);

  /**
   * Currency info from the subscription
   */
  const currency = useMemo(
    () => ({
      code: subscription?.currencyCode || selectedTenant?.currencyCode || "GBP",
      symbol:
        subscription?.currencySymbol || selectedTenant?.currencySymbol || "£",
    }),
    [
      subscription?.currencyCode,
      subscription?.currencySymbol,
      selectedTenant?.currencyCode,
      selectedTenant?.currencySymbol,
    ],
  );

  const value = useMemo(
    () => ({
      // State
      subscription,
      features,
      loading,
      error,

      // Tier flags
      isFreeTier,
      isProTier,
      isEnterpriseTier,
      isTrial,
      isTrialExpired,
      trialDaysRemaining,

      // Currency
      currency,

      // Actions
      canAccess,
      isAtLeastTier,
      refreshSubscription,
    }),
    [
      subscription,
      features,
      loading,
      error,
      isFreeTier,
      isProTier,
      isEnterpriseTier,
      isTrial,
      isTrialExpired,
      trialDaysRemaining,
      currency,
      canAccess,
      isAtLeastTier,
      refreshSubscription,
    ],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to access subscription context
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
};

export default SubscriptionContext;
